<template>
  <MaskLayer>
    <Slider class="w-75" @close="close">
      <div class="container-fluid p-0 mw-100 flex-grow-1 overflow-auto">
        <div class="h-100 overflow-auto row no-gutters">
          <div class="col-3 h-100 overflow-auto border border-right border-light">
            <div class="w-100 d-flex align-items-center flex-column px-3 my-2">
              <div class="my-2 image-panel image-panel-3 rounded rounded-circle">
                <img :src="user.avatar" alt="">
              </div>
              <h6 class="my-1">
                {{ user.name }}
              </h6>
              <div class="my-1 font-size-md">
                {{ user.email }}
              </div>
            </div>
            <div class="px-3">
              <b-button
                :variant="tab === 'AccountInfo' ? 'light' : 'outline-light'"
                class="w-100 py-0 mb-2 d-flex align-items-center text-secondary position-relative"
                draggable=""
                @click="tab = 'AccountInfo'"
              >
                <div class="image-panel image-panel-0 bg-none">
                  <span v-if="infoValid" class="material-icons-outlined md-18">person</span>
                  <i v-else class="material-icons-outlined text-danger">error_outline</i>
                </div>
                <span class="font-size-md text-truncate">{{ $t('frame.accountSetting.title') }}</span>
              </b-button>
              <b-button
                :variant="tab === 'AccountPassword' ? 'light' : 'outline-light'"
                class="w-100 py-0 mb-2 d-flex align-items-center text-secondary position-relative"
                draggable=""
                @click="tab = 'AccountPassword'"
              >
                <div class="image-panel image-panel-0 bg-none">
                  <span v-if="passwordValid" class="material-icons-outlined md-18">vpn_key</span>
                  <i v-else class="material-icons-outlined text-danger">error_outline</i>
                </div>
                <span class="font-size-md text-truncate">{{ $t('frame.passwordChange.title') }}</span>
              </b-button>
            </div>
          </div>
          <AccountInfo v-if="tab === 'AccountInfo'" v-model="editUser" :error.sync="error.info" @input="onInfoInput" />
          <AccountPassword v-if="tab === 'AccountPassword'" v-model="editPassword" :error.sync="error.password" @input="onPasswordInput" />
        </div>
      </div>
      <FixedBottomBtn class="bg-white p-3">
        <div class="d-flex justify-content-between">
          <div />
          <div>
            <button class="ml-2 btn btn-sm btn-outline-light rounded-pill" @click="close">
              <span v-t="'common.cancel'" class="px-3 text-secondary" />
            </button>
            <button class="ml-2 btn btn-sm btn-success rounded-pill" @click="save">
              <span v-t="'common.save'" class="px-3" />
            </button>
          </div>
        </div>
      </FixedBottomBtn>
    </Slider>
  </MaskLayer>
</template>

<script>
import MaskLayer from '@/components/MaskLayer.vue';
import Slider from '@/components/Slider.vue';
import FixedBottomBtn from '@/components/FixedBottomBtn.vue';
import AccountInfo from '@/components/Account/AccountInfo.vue';
import AccountPassword from '@/components/Account/AccountPassword.vue';
import PermissionService from '@/services/PermissionService';
import { PasswordFormatValidate, MobileFormatValidate } from '@/services/AccountService';
import FileManagerWebService from '@/webServices/FileManagerWebService';

export default {
  components: {
    MaskLayer,
    Slider,
    FixedBottomBtn,
    AccountInfo,
    AccountPassword
  },
  data() {
    return {
      tab: '',

      user: {},
      editUser: {},
      editPassword: {},

      error: {
        info: {
          name: '',
          mobile: '',
        },
        password: {
        }
      },

      saving: false
    };
  },
  computed: {
    infoValid() {
      return Object.keys(this.error.info).every((k) => (this.error.info[k] || '') === '');
    },
    passwordValid() {
      if (Object.keys(this.error.password).length === 0) return true;
      return Object.keys(this.error.password).every((k) => (this.error.password[k] || '') === '');
    }
  },
  async created() {
    await this.LoadAsync();
  },
  methods: {
    close() {
      this.$emit('close');
    },
    validate() {
      return [this.validateName(), this.validateMobile(), this.validatePassword()].every((v) => v);
    },
    async save() {
      if (!this.saving) {
        try {
          this.saving = true;

          if (!this.validate()) {
            return;
          }

          let { avatar } = this.user;

          // 有重新上傳頭像
          if (avatar !== this.editUser.avatar) {
            try {
              await this.$loading(async () => {
                const result = await FileManagerWebService.UploadAsync({
                  storageType: 0,
                  files: [
                    {
                      name: this.$uuid.v4(),
                      extension: 'jpeg',
                      path: 'permission/avatar',
                      content: this.editUser.avatar.split(',')[1]
                    }
                  ]
                });

                avatar = result.data && result.data.data && result.data.data[0].fileUrl;
              });
            } catch {
              this.$notify.danger(this.$t('frame.accountSetting.form.avatarUploadFaild'));
              throw this.$t('frame.accountSetting.form.avatarUploadFaild');
            }
          }

          await this.$loading(async () => {
            try {
              await PermissionService.UpdateLoginUserAsync({
                name: this.editUser.name,
                avatar,
                mobile: this.editUser.mobile,
                password: this.editPassword.old,
                newPassword: this.editPassword.new
              });

              this.$notify.saveSuccess();
              await this.LoadAsync();
              this.$emit('confirm');
            } catch (err) {
              this.saveErrorProcess((err.response || {}).data || {});
              this.$notify.saveFailed();
            }
          });
        } finally {
          this.saving = false;
        }
      }
    },
    saveErrorProcess(data) {
      const passwordError = {};

      if ((data.status || '') !== '') {
        switch (data.status) {
          case '401':
            passwordError.old = this.$t('frame.passwordChange.form.oldPassword.validateFail');
            break;
          case '409':
            passwordError.new = this.$t('frame.passwordChange.form.newPassword.historyFail');
            break;
          default:
            break;
        }
      }

      this.error.password = passwordError;
    },
    onInfoInput() {
      if ((this.error.info.name || '') !== '') {
        this.validateName();
      }

      if ((this.error.info.mobile || '') !== '') {
        this.validateMobile();
      }
    },
    validateName() {
      this.error.info.name = '';
      if ((this.editUser.name || '').trim().length === 0) {
        this.error.info.name = this.$t('frame.accountSetting.form.name.placeholder');
      }

      if ((this.editUser.name || '').trim().length > 50) {
        this.error.info.name = this.$t('frame.accountSetting.form.name.overlimit');
      }
      return (this.error.info.name || '') === '';
    },
    validateMobile() {
      this.error.info.mobile = '';
      if ((this.editUser.mobile || '').trim().length !== 0) {
        if (!MobileFormatValidate(this.editUser.mobile)) {
          this.error.info.mobile = this.$t('frame.accountSetting.form.mobile.invalid');
        }
      }
      return (this.error.info.mobile || '') === '';
    },
    onPasswordInput() {
      if (Object.keys(this.error.password).length !== 0) {
        this.validatePassword();
      }
    },
    validatePassword() {
      if ((this.editPassword.old || '').trim().length === 0
      && (this.editPassword.new || '').trim().length === 0
      && (this.editPassword.newConfirm || '').trim().length === 0) {
        this.error.password = {};
        return true;
      }

      const error = {};

      if ((this.editPassword.old || '').trim().length === 0) {
        error.old = this.$t('auth.roleEdit.pleaseInput');
      }

      if ((this.editPassword.new || '').trim().length === 0) {
        error.new = this.$t('register.password.placeholder');
      } else if (this.editPassword.new === this.user.account) {
        error.new = this.$t('register.password.feedback.accountDuplicate');
      } else if (!PasswordFormatValidate(this.editPassword.new)) {
        error.new = this.$t('register.password.feedback.regex');
      }

      if ((this.editPassword.newConfirm || '').trim().length === 0) {
        error.newConfirm = this.$t('register.confirmPassword.placeholder');
      } else if (this.editPassword.newConfirm !== this.editPassword.new) {
        error.newConfirm = this.$t('register.confirmPassword.feedback.equal');
      }

      this.error.password = error;
      return Object.keys(error).length === 0;
    },
    async LoadAsync() {
      await this.$loading(async () => {
        const originTab = this.tab;

        const user = await PermissionService.GetLoginUserProfileAsync();
        this.user = { ...user };
        this.editUser = { ...user };
        this.editPassword = {};

        this.setTab(originTab);
      });
    },
    async setTab(originTab) {
      await this.$loading(async () => {
        await this.$nextTick(() => {
          this.tab = originTab || 'AccountInfo';
        });
      });
    },
  }
};
</script>
